import { PriceStrategy } from '@snap-types/models/enums';
import {
  LastWinContributionMargin,
  LastWinGrossMargin,
  LastWinQuantity,
  LastWinUnitCost,
  LastWinUnitPrice,
  LastWinUnitVariableCosts,
} from '@ui/features/quote-builder/components/QuoteLine/LastWin/fields';
import { CostBreaks } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineInfo/CostBreaks';
import { CostEstimator } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineInfo/CostEstimator';
import { CostOverview } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineInfo/CostOverview';
import { DiscountPriceBook } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineInfo/DiscountPriceBook';
import { MarketPriceBook } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineInfo/MarketPriceBook';
import { MetricsDevView } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineInfo/MetricsDevView';
import { OrderHistory } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineInfo/OrderHistory';
import {
  CAPPriceSegments,
  PriceSegments,
} from '@ui/features/quote-builder/components/QuoteLine/QuoteLineInfo/PriceSegments';
import { QuoteHistory } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineInfo/QuoteHistory';
import type { TabComponentType } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineInfo/types';
import { BaseGrossMargin } from '@ui/features/quote-builder/components/QuotePrice/BaseGrossMargin';
import {
  ContributionMarginPercent,
  ContributionMarginPercentReadOnly,
} from '@ui/features/quote-builder/components/QuotePrice/ContributionMargin';
import { EditableUnitCost } from '@ui/features/quote-builder/components/QuotePrice/EditableUnitCost';
import { FinalGrossMargin } from '@ui/features/quote-builder/components/QuotePrice/FinalGrossMargin';
import {
  GrossMarginPercent,
  GrossMarginPercentReadOnly,
} from '@ui/features/quote-builder/components/QuotePrice/GrossMargin';
import { PriceAdjustmentAmount } from '@ui/features/quote-builder/components/QuotePrice/PriceAdjustmentAmount';
import { Quantity } from '@ui/features/quote-builder/components/QuotePrice/Quantity';
import { UnitCost } from '@ui/features/quote-builder/components/QuotePrice/UnitCost';
import { UnitPrice } from '@ui/features/quote-builder/components/QuotePrice/UnitPrice';
import { UnitVariableCosts } from '@ui/features/quote-builder/components/QuotePrice/UnitVariableCosts';

interface PriceStrategyUIConfigData {
  badgeColor: string;
  badgeLabel: string;
  quoteLineTabs: TabComponentType[];
  quotePriceFields: React.ComponentType[];
  lastWinFields: React.ComponentType[];
}

// NOTE: quote line tabs are displayed consistently in this order, if present:
//    CostOverview
//    QuoteHistory
//    PriceSegments / CAP PriceSegments / MarketPriceBook
//    CostEstimator
//    OrderHistory
//    CostBreaks

type PriceStrategyUIConfig = Record<PriceStrategy, PriceStrategyUIConfigData>;

export const priceStrategyUIConfig = {
  [PriceStrategy.PriceSegmentation]: {
    badgeColor: 'teal',
    badgeLabel: 'Price Segmentation',
    quoteLineTabs: [
      QuoteHistory,
      PriceSegments,
      OrderHistory,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [Quantity, PriceAdjustmentAmount, UnitPrice],
    lastWinFields: [
      LastWinQuantity,
      LastWinUnitCost,
      LastWinUnitPrice,
      LastWinGrossMargin,
    ],
  },
  [PriceStrategy.TargetGrossMargin]: {
    badgeColor: 'green',
    badgeLabel: 'Target Gross Margin',
    quoteLineTabs: [
      CostOverview,
      QuoteHistory,
      CostEstimator,
      OrderHistory,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [
      Quantity,
      UnitCost,
      UnitPrice,
      GrossMarginPercent,
      ContributionMarginPercentReadOnly,
    ],
    lastWinFields: [
      LastWinQuantity,
      LastWinUnitCost,
      LastWinUnitPrice,
      LastWinGrossMargin,
      LastWinContributionMargin,
    ],
  },
  [PriceStrategy.TargetContributionMargin]: {
    badgeColor: 'blue',
    badgeLabel: 'Target Contribution Margin',
    quoteLineTabs: [
      CostOverview,
      QuoteHistory,
      CostEstimator,
      OrderHistory,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [
      Quantity,
      UnitVariableCosts,
      UnitPrice,
      ContributionMarginPercent,
      GrossMarginPercentReadOnly,
    ],
    lastWinFields: [
      LastWinQuantity,
      LastWinUnitVariableCosts,
      LastWinUnitPrice,
      LastWinContributionMargin,
      LastWinGrossMargin,
    ],
  },
  [PriceStrategy.FixedDiscount]: {
    badgeColor: 'red',
    badgeLabel: 'Discount Price',
    quoteLineTabs: [
      QuoteHistory,
      DiscountPriceBook,
      CostEstimator,
      OrderHistory,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [Quantity, UnitPrice],
    lastWinFields: [LastWinQuantity, LastWinUnitPrice],
  },
  [PriceStrategy.FixedMarket]: {
    badgeColor: 'orange',
    badgeLabel: 'Market Price',
    quoteLineTabs: [
      QuoteHistory,
      MarketPriceBook,
      OrderHistory,
      CostEstimator,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [Quantity, UnitPrice],
    lastWinFields: [LastWinQuantity, LastWinUnitPrice],
  },
  [PriceStrategy.FixedCustomer]: {
    badgeColor: 'yellow',
    badgeLabel: 'Customer Price',
    quoteLineTabs: [
      QuoteHistory,
      CostEstimator,
      OrderHistory,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [Quantity, UnitPrice],
    lastWinFields: [LastWinQuantity, LastWinUnitPrice],
  },
  [PriceStrategy.FixedDefault]: {
    badgeColor: 'orange',
    badgeLabel: 'Default Price',
    quoteLineTabs: [
      QuoteHistory,
      OrderHistory,
      CostEstimator,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [Quantity, UnitPrice],
    lastWinFields: [LastWinQuantity, LastWinUnitPrice],
  },
  [PriceStrategy.CAPPriceSegmentation]: {
    badgeColor: 'teal',
    badgeLabel: 'Price Segmentation',
    quoteLineTabs: [
      CostOverview,
      QuoteHistory,
      CAPPriceSegments,
      CostEstimator,
      OrderHistory,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [
      Quantity,
      UnitCost,
      BaseGrossMargin,
      PriceAdjustmentAmount,
      UnitPrice,
      FinalGrossMargin,
    ],
    lastWinFields: [
      LastWinQuantity,
      LastWinUnitCost,
      LastWinUnitPrice,
      LastWinGrossMargin,
    ],
  },
  [PriceStrategy.UserPrice]: {
    badgeColor: 'orange',
    badgeLabel: 'Custom Price',
    quoteLineTabs: [
      QuoteHistory,
      OrderHistory,
      CostEstimator,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [Quantity, UnitPrice],
    lastWinFields: [LastWinQuantity, LastWinUnitPrice],
  },
  [PriceStrategy.NewPartUserPrice]: {
    badgeColor: 'orange',
    badgeLabel: 'Custom Price',
    quoteLineTabs: [CostBreaks, MetricsDevView],
    quotePriceFields: [Quantity, UnitPrice],
    lastWinFields: [],
  },
  [PriceStrategy.UserImport]: {
    badgeColor: 'orange',
    badgeLabel: 'Imported Price',
    quoteLineTabs: [QuoteHistory, OrderHistory, CostBreaks, MetricsDevView],
    quotePriceFields: [Quantity, UnitPrice],
    lastWinFields: [LastWinQuantity, LastWinUnitPrice],
  },
  [PriceStrategy.RAFFixedMarket]: {
    badgeColor: 'orange',
    badgeLabel: 'Market Price',
    quoteLineTabs: [
      QuoteHistory,
      MarketPriceBook,
      OrderHistory,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [Quantity, UnitPrice],
    lastWinFields: [LastWinQuantity, LastWinUnitPrice],
  },
  [PriceStrategy.RAFTargetGrossMargin]: {
    badgeColor: 'green',
    badgeLabel: 'Target Gross Margin',
    quoteLineTabs: [
      CostOverview,
      QuoteHistory,
      CostEstimator,
      OrderHistory,
      CostBreaks,
      MetricsDevView,
    ],
    quotePriceFields: [Quantity, UnitCost, UnitPrice, GrossMarginPercent],
    lastWinFields: [
      LastWinQuantity,
      LastWinUnitCost,
      LastWinUnitPrice,
      LastWinGrossMargin,
    ],
  },
  [PriceStrategy.NewPart]: {
    badgeColor: 'green',
    badgeLabel: 'New Part',
    quoteLineTabs: [CostEstimator, CostBreaks, MetricsDevView],
    quotePriceFields: [Quantity, UnitCost, UnitPrice, GrossMarginPercent],
    lastWinFields: [],
  },
  [PriceStrategy.NewPartUserCost]: {
    badgeColor: 'green',
    badgeLabel: 'Third Party Part',
    quoteLineTabs: [CostBreaks, MetricsDevView],
    quotePriceFields: [
      Quantity,
      EditableUnitCost,
      UnitPrice,
      GrossMarginPercent,
    ],
    lastWinFields: [],
  },
} satisfies PriceStrategyUIConfig;
